// Destinations page — a visual hub-and-spoke view of where server-side
// events flow. Datacrow sits in the center (the feather mark); Shopify +
// the storefront pixel feed in from the left as sources; each configured
// destination fans out to the right with a live/paused status and a
// connector that lights up green once events are actually delivering.
//
// Clicking a destination node opens a modal with that destination's setup
// form (reused verbatim from Onboarding so the save/test/validation logic
// never drifts). "Add destination" opens a picker of the not-yet-connected
// destinations, then the same modal.
//
// Why a hub diagram over the old form list: merchants think in terms of
// "what's connected and is it working", not "here are six forms". The
// visual answers that at a glance — green spokes = healthy, gray =
// configured-but-unverified, amber = paused. The forms are still the same
// underneath; they're just reached by clicking a node now.
//
// Layout note: the diagram is authored in a fixed 640-unit design space and
// centered, so the absolutely-positioned HTML nodes line up exactly with
// the SVG connectors (1 design unit = 1px). The wrapper allows horizontal
// scroll on the rare embedded width narrower than that.

import { useEffect, useState } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import {
  Page,
  Modal,
  Text,
  BlockStack,
  InlineStack,
  Spinner,
  Banner,
  Badge,
  Button,
  Card,
  Box,
  Link,
} from "@shopify/polaris";
import { apiFetch, ApiError, getCached, type Shopify } from "./api";
import { HELP_BASE_URL, DESTINATION_DOCS } from "./help-links";
import { replayNote } from "./replay-note";
import { DESTINATION_CARDS } from "./destination-cards";
import { DESTINATION_CATALOG, DESTINATION_TYPES } from "./destination-catalog";
import { allTypesFrom } from "./available-types";
import {
  DestinationOverlapWarning,
  CompetitorOverlapBanner,
  type DestinationCardExistingView,
  type StoreContext,
} from "./Onboarding";

interface DestinationView extends DestinationCardExistingView {
  createdAt: string;
  updatedAt: string;
  // Most-recent failed delivery (GET /api/destinations). Newer than
  // lastSuccessAt → the destination is currently failing; drives the hub's
  // "action needed" state. Optional for forward-compat / cached responses.
  lastFailureAt?: string | null;
}

type DestinationType = DestinationView["type"];

interface DestinationsProps {
  shop: string;
  onBack?: () => void;
}

// Display labels + a short form for the compact hub nodes, from the catalog.
// This was a literal map that src/types/destination.test.ts cross-checked by
// reading THIS FILE's source with a regex, because the backend kept a second
// copy for its emails. Both now read one object.
const DESTINATION_META: Record<DestinationType, { label: string; short: string }> =
  Object.fromEntries(
    DESTINATION_TYPES.map((t) => [
      t,
      { label: DESTINATION_CATALOG[t].label, short: DESTINATION_CATALOG[t].short },
    ]),
  ) as Record<DestinationType, { label: string; short: string }>;


export function Destinations({ shop, onBack }: DestinationsProps) {
  const shopify = useAppBridge();
  // Seed from the SWR cache (App's bootstrap already fetched /api/destinations)
  // so this page paints instantly; reload() revalidates in the background.
  const cached = getCached<{
    destinations: DestinationView[];
    storeContext: StoreContext;
    availableTypes?: string[];
  }>("/api/destinations");
  const [destinations, setDestinations] = useState<DestinationView[] | null>(
    cached?.destinations ?? null,
  );
  const [storeContext, setStoreContext] = useState<StoreContext | null>(
    cached?.storeContext ?? null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [availableTypes, setAvailableTypes] = useState<string[] | undefined>(
    cached?.availableTypes,
  );

  // Which destination's setup modal is open (null = closed). The picker
  // modal is a separate boolean so "Add destination" → pick → setup is a
  // clean two-step without intermediate state.
  const [activeType, setActiveType] = useState<DestinationType | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const reload = async () => {
    try {
      const resp = await apiFetch<{
        destinations: DestinationView[];
        storeContext: StoreContext;
        availableTypes?: string[];
      }>(shopify, "/api/destinations");
      setDestinations(resp.destinations);
      setStoreContext(resp.storeContext);
      setAvailableTypes(resp.availableTypes);
      setLoadError(null);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setLoadError(msg);
    }
  };

  useEffect(() => {
    void reload();
    // shopify is stable per App Bridge contract
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page = (children: React.ReactNode) => (
    <Page
      title="Destinations"
      subtitle={shop}
      backAction={onBack ? { content: "Back", onAction: onBack } : undefined}
      primaryAction={{
        content: "Add destination",
        onAction: () => setPickerOpen(true),
        disabled: destinations === null,
      }}
      secondaryActions={[
        { content: "Setup guides", url: HELP_BASE_URL, external: true },
      ]}
    >
      {children}
    </Page>
  );

  if (loadError) {
    return page(
      <Banner tone="critical" title="Couldn't load destinations">
        <p>{loadError}</p>
      </Banner>,
    );
  }

  if (destinations === null) {
    return page(
      <Card>
        <BlockStack gap="200" align="center">
          <Spinner accessibilityLabel="Loading destinations" size="large" />
          <Text as="p" tone="subdued">Loading…</Text>
        </BlockStack>
      </Card>,
    );
  }

  const configured = destinations;
  const allTypes = allTypesFrom(availableTypes);
  const unconfiguredTypes = allTypes.filter(
    (t) => !configured.some((d) => d.type === t),
  );
  const activeExisting = activeType
    ? configured.find((d) => d.type === activeType)
    : undefined;

  return page(
    <BlockStack gap="400">
      <CompetitorOverlapBanner storeContext={storeContext ?? undefined} />

      {configured.some((d) => d.dryRun) && (
        <Banner tone="warning" title="Some destinations are in dry-run mode">
          <p>
            Events to{" "}
            <strong>
              {configured
                .filter((d) => d.dryRun)
                .map((d) => DESTINATION_META[d.type].short)
                .join(", ")}
            </strong>{" "}
            are being logged, not sent (and their Test buttons only simulate a
            send). Set the matching <code>*_DRY_RUN=false</code> on{" "}
            <strong>both</strong> your web and worker services to deliver for
            real.
          </p>
        </Banner>
      )}

      <FlowHub
        destinations={configured}
        onAddClick={() => setPickerOpen(true)}
        onNodeClick={(t) => setActiveType(t)}
      />

      <DestinationPickerModal
        open={pickerOpen}
        unconfigured={unconfiguredTypes}
        onClose={() => setPickerOpen(false)}
        onPick={(t) => {
          setPickerOpen(false);
          setActiveType(t);
        }}
      />

      <DestinationSetupModal
        type={activeType}
        existing={activeExisting}
        shopify={shopify}
        storeContext={storeContext}
        onClose={() => setActiveType(null)}
        onChanged={() => void reload()}
      />
    </BlockStack>,
  );
}

// ---------- The visual hub ----------

// Design-space constants. Everything is authored in a 640-wide coordinate
// system so the SVG connectors and the HTML nodes share one set of numbers.
const HUB_W = 640;
const NODE_W = 176;
const NODE_H = 60;
const ROW_GAP = 20;
const HUB_CX = 320;
const HUB_R = 42;
const SRC_RIGHT = 188; // right edge x of source nodes
const DST_LEFT = HUB_W - 188; // left edge x of destination nodes (452)
const V_PAD = 24; // top/bottom breathing room

function FlowHub({
  destinations,
  onAddClick,
  onNodeClick,
}: {
  destinations: DestinationView[];
  onAddClick: () => void;
  onNodeClick: (t: DestinationType) => void;
}) {
  const sources = [
    { key: "shopify", label: "Shopify", sub: "Orders + checkouts" },
    { key: "pixel", label: "Storefront pixel", sub: "Browser events" },
  ];
  const nSrc = sources.length;
  // Spokes list top-to-bottom in alphabetical order by short label, so the
  // order is stable + scannable regardless of when each was configured.
  const ordered = [...destinations].sort((a, b) =>
    DESTINATION_META[a.type].short.localeCompare(DESTINATION_META[b.type].short),
  );
  const nDst = ordered.length;

  const groupH = (n: number) => (n > 0 ? n * NODE_H + (n - 1) * ROW_GAP : 0);
  const srcH = groupH(nSrc);
  const dstH = groupH(nDst);
  const innerH = Math.max(srcH, dstH, 160);
  const H = innerH + V_PAD * 2;
  const centerY = H / 2;

  // Vertical center of node i within a group that's centered on centerY.
  const nodeCenterY = (i: number, n: number) =>
    centerY - groupH(n) / 2 + NODE_H / 2 + i * (NODE_H + ROW_GAP);

  const srcCenters = sources.map((_, i) => nodeCenterY(i, nSrc));
  const dstCenters = ordered.map((_, i) => nodeCenterY(i, nDst));

  return (
    <Card>
      <div style={{ overflowX: "auto" }}>
        <div
          style={{
            position: "relative",
            width: HUB_W,
            height: H,
            maxWidth: "100%",
            margin: "0 auto",
          }}
        >
          {/* Flowing dashes travel along each connector toward its arrowhead,
              visualizing live data movement. Negative dashoffset = motion in
              the path's draw direction (source→hub, hub→destination). Only
              live spokes carry the .dc-flow class; reduced-motion stops it. */}
          <style>{`
            @keyframes dcFlow { to { stroke-dashoffset: -14; } }
            .dc-flow { animation: dcFlow 0.5s linear infinite; }
            @media (prefers-reduced-motion: reduce) { .dc-flow { animation: none; } }
          `}</style>
          <svg
            viewBox={`0 0 ${HUB_W} ${H}`}
            width="100%"
            height={H}
            style={{ position: "absolute", inset: 0 }}
            aria-hidden="true"
          >
            {/* Arrowheads encode flow direction: data flows IN from sources
                to the hub, and OUT from the hub to each destination. */}
            <defs>
              {[
                ["arrowGreen", "#2b9b83"],
                ["arrowGray", "#8a8a8a"],
                ["arrowAmber", "#b9770f"],
                ["arrowRed", "#b42318"],
              ].map(([id, color]) => (
                <marker
                  key={id}
                  id={id}
                  viewBox="0 0 10 10"
                  refX={8}
                  refY={5}
                  markerWidth={4}
                  markerHeight={4}
                  orient="auto"
                >
                  <path d="M0 0 L10 5 L0 10 z" fill={color} />
                </marker>
              ))}
            </defs>
            {/* source → hub connectors (always green: sources are always on).
                Arrow points INTO the hub = inflow. */}
            {srcCenters.map((sy, i) => (
              <path
                key={`s${i}`}
                className="dc-flow"
                d={`M ${SRC_RIGHT} ${sy} C ${SRC_RIGHT + 64} ${sy} ${
                  HUB_CX - HUB_R - 42
                } ${centerY} ${HUB_CX - HUB_R} ${centerY}`}
                fill="none"
                stroke="#2b9b83"
                strokeWidth={2}
                strokeDasharray="3 4"
                markerEnd="url(#arrowGreen)"
              />
            ))}
            {/* hub → destination connectors. Arrow points INTO the
                destination = outflow. Green when live, gray otherwise. */}
            {ordered.map((d, i) => {
              const dy = dstCenters[i]!;
              const st = STATUS_STYLE[hubStatus(d)];
              return (
                <path
                  key={`d${d.type}`}
                  className={st.flow ? "dc-flow" : undefined}
                  d={`M ${HUB_CX + HUB_R} ${centerY} C ${HUB_CX + HUB_R + 42} ${centerY} ${
                    DST_LEFT - 42
                  } ${dy} ${DST_LEFT} ${dy}`}
                  fill="none"
                  stroke={st.color}
                  strokeWidth={2}
                  strokeDasharray="3 4"
                  markerEnd={`url(#${st.marker})`}
                />
              );
            })}
            {/* hub circle — Datacrow brand badge (mint fill, navy ring) */}
            <circle
              cx={HUB_CX}
              cy={centerY}
              r={HUB_R}
              fill="#e6f1f0"
              stroke="#00344f"
              strokeWidth={2}
            />
          </svg>

          {/* Datacrow crow mark, centered in the hub. Rendered as an HTML
              <img> overlay rather than an SVG <image> so the continuous spoke
              animation doesn't re-rasterize this raster every frame — that
              per-frame repaint was heavy enough to make the cursor flicker. */}
          <img
            src="/datacrow-mark.png"
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              left: HUB_CX - 28,
              top: centerY - 28,
              width: 56,
              height: 56,
            }}
          />

          {/* section labels */}
          <div style={hubLabelStyle({ left: 0, top: V_PAD - 18 })}>SOURCES</div>
          <div
            style={hubLabelStyle({
              right: 0,
              top: V_PAD - 18,
              textAlign: "right",
              width: NODE_W,
            })}
          >
            DESTINATIONS
          </div>
          <div
            style={{
              position: "absolute",
              left: HUB_CX - 60,
              top: centerY + HUB_R + 6,
              width: 120,
              textAlign: "center",
              fontSize: 13,
              fontWeight: 500,
              color: "var(--p-color-text, #202223)",
            }}
          >
            Datacrow
          </div>

          {/* source nodes */}
          {sources.map((s, i) => (
            <div
              key={s.key}
              style={nodeBoxStyle({
                left: 0,
                top: srcCenters[i]! - NODE_H / 2,
              })}
            >
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {s.label}
              </Text>
              <Text as="span" variant="bodySm" tone="subdued">
                {s.sub}
              </Text>
            </div>
          ))}

          {/* destination nodes */}
          {ordered.map((d, i) => {
            const meta = DESTINATION_META[d.type];
            return (
              <button
                key={d.type}
                type="button"
                onClick={() => onNodeClick(d.type)}
                style={{
                  ...nodeBoxStyle({
                    right: 0,
                    top: dstCenters[i]! - NODE_H / 2,
                  }),
                  cursor: "pointer",
                  textAlign: "left",
                  font: "inherit",
                }}
              >
                <InlineStack align="space-between" blockAlign="center" wrap={false}>
                  <Text as="span" variant="bodyMd" fontWeight="medium">
                    {meta.short}
                  </Text>
                  <StatusBadge destination={d} />
                </InlineStack>
                <Text as="span" variant="bodySm" tone="subdued">
                  {d.lastSuccessAt
                    ? `Last event ${formatRelative(d.lastSuccessAt)}`
                    : "No events yet"}
                </Text>
              </button>
            );
          })}

          {/* empty state: no destinations yet */}
          {ordered.length === 0 && (
            <div
              style={{
                position: "absolute",
                right: 0,
                top: centerY - NODE_H / 2,
                width: NODE_W,
              }}
            >
              <button
                type="button"
                onClick={onAddClick}
                style={{
                  ...nodeBoxStyle({ right: 0, top: 0 }),
                  position: "static",
                  cursor: "pointer",
                  borderStyle: "dashed",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  font: "inherit",
                }}
              >
                <Text as="span" variant="bodySm" tone="subdued">
                  + Connect your first destination
                </Text>
              </button>
            </div>
          )}
        </div>
      </div>

      {destinations.length > 0 && (
        <Legend />
      )}
    </Card>
  );
}

function Legend() {
  const item = (color: string, label: string) => (
    <InlineStack gap="150" blockAlign="center">
      <span
        style={{ width: 18, height: 2, background: color, display: "inline-block" }}
      />
      <Text as="span" variant="bodySm" tone="subdued">
        {label}
      </Text>
    </InlineStack>
  );
  return (
    <div style={{ marginTop: 12 }}>
      <InlineStack gap="400" wrap>
        {item("#2b9b83", "Live: events delivering")}
        {item("#8a8a8a", "Connected: not verified yet")}
        {item("#b9770f", "Test mode: logged, not sent")}
        {item("#b42318", "Action needed: recent failures")}
      </InlineStack>
    </div>
  );
}

function StatusBadge({ destination }: { destination: DestinationView }) {
  switch (hubStatus(destination)) {
    case "error":
      return <Badge tone="critical">Action needed</Badge>;
    case "test_mode":
      return <Badge tone="attention">Test mode</Badge>;
    case "live":
      return <Badge tone="success">Live</Badge>;
    case "paused":
      return <Badge tone="warning">Paused</Badge>;
    case "connected":
    default:
      return <Badge tone="info">Connected</Badge>;
  }
}

type HubStatus = "error" | "test_mode" | "live" | "connected" | "paused";

// Resolve the hub status for a destination. Order matters: a currently-failing
// destination (a failure newer than its last success) outranks live/test-mode,
// and a paused one isn't counted as "failing".
function hubStatus(d: DestinationView): HubStatus {
  if (!d.enabled) return "paused";
  if (isFailing(d)) return "error";
  if (d.dryRun) return "test_mode";
  if (d.lastSuccessAt) return "live";
  return "connected";
}

function isFailing(d: DestinationView): boolean {
  if (!d.lastFailureAt) return false;
  if (!d.lastSuccessAt) return true; // failed, never succeeded
  return new Date(d.lastFailureAt) > new Date(d.lastSuccessAt);
}

// Per-status visual for the spokes: connector stroke + arrowhead marker + whether
// the dashes animate (only a healthy, live spoke flows).
const STATUS_STYLE: Record<HubStatus, { color: string; marker: string; flow: boolean }> = {
  live: { color: "#2b9b83", marker: "arrowGreen", flow: true },
  test_mode: { color: "#b9770f", marker: "arrowAmber", flow: false },
  error: { color: "#b42318", marker: "arrowRed", flow: false },
  connected: { color: "#8a8a8a", marker: "arrowGray", flow: false },
  paused: { color: "#8a8a8a", marker: "arrowGray", flow: false },
};

// Shared inline-style helpers for the absolutely-positioned hub elements.
function nodeBoxStyle(pos: {
  left?: number;
  right?: number;
  top: number;
}): React.CSSProperties {
  return {
    position: "absolute",
    left: pos.left,
    right: pos.right,
    top: pos.top,
    width: NODE_W,
    height: NODE_H,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 2,
    padding: "8px 12px",
    background: "var(--p-color-bg-surface, #ffffff)",
    border: "1px solid var(--p-color-border, #e1e1e1)",
    borderRadius: "var(--p-border-radius-300, 8px)",
  };
}

function hubLabelStyle(pos: {
  left?: number;
  right?: number;
  top: number;
  textAlign?: "left" | "right";
  width?: number;
}): React.CSSProperties {
  return {
    position: "absolute",
    left: pos.left,
    right: pos.right,
    top: pos.top,
    width: pos.width,
    textAlign: pos.textAlign,
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.04em",
    color: "var(--p-color-text-subdued, #6d7175)",
  };
}

// ---------- Add-destination picker modal ----------

function DestinationPickerModal({
  open,
  unconfigured,
  onClose,
  onPick,
}: {
  open: boolean;
  unconfigured: DestinationType[];
  onClose: () => void;
  onPick: (t: DestinationType) => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title="Add a destination">
      <Modal.Section>
        {unconfigured.length === 0 ? (
          <Text as="p" tone="subdued">
            All available destinations are already connected. Click any node in
            the hub to edit it.
          </Text>
        ) : (
          <BlockStack gap="200">
            {unconfigured.map((t) => (
              <Button
                key={t}
                fullWidth
                textAlign="left"
                onClick={() => onPick(t)}
              >
                {DESTINATION_META[t].label}
              </Button>
            ))}
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

// ---------- Destination setup modal ----------

function DestinationSetupModal({
  type,
  existing,
  shopify,
  storeContext,
  onClose,
  onChanged,
}: {
  type: DestinationType | null;
  existing: DestinationView | undefined;
  shopify: Shopify;
  storeContext: StoreContext | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  if (!type) return null;
  const meta = DESTINATION_META[type];

  // NOTE: do NOT use titleHidden here. With the title hidden, Polaris still
  // renders its floating close (X) button in the top-right of the content
  // area — which collides with any control we place at the top of the
  // body (the Pause/Resume button did exactly this). Showing the real
  // title bar moves the X up into the header where it belongs, and all
  // body content renders cleanly below it.
  return (
    <Modal open onClose={onClose} title={meta.label}>
      <Modal.Section>
        <BlockStack gap="300">
          <InlineStack gap="100" blockAlign="center">
            <Text as="span" tone="subdued" variant="bodySm">
              Need help connecting {meta.short}?
            </Text>
            <Link url={DESTINATION_DOCS[type]} external>
              View the setup guide
            </Link>
          </InlineStack>
          <DestinationOverlapWarning
            destinationType={type}
            storeContext={storeContext ?? undefined}
          />
          {existing?.dryRun && (
            <Banner tone="warning" title="Dry-run mode: events are not being sent">
              <p>
                Datacrow is logging {meta.short} events instead of delivering
                them (the Test button only simulates a send). Set{" "}
                <code>{type.toUpperCase()}_DRY_RUN=false</code> on{" "}
                <strong>both</strong> your web and worker services, then
                redeploy.
              </p>
            </Banner>
          )}
          {existing && isFailing(existing) && (
            <Banner tone="critical" title="Recent delivery failures">
              <p>
                Some events to {meta.short} have failed recently. Check that the
                credentials below are still valid, then send a test event to
                confirm. You can see the recent attempts in the activity feed.
              </p>
            </Banner>
          )}
          {existing && (
            <PauseResumeRow
              shopify={shopify}
              destination={existing}
              onChanged={onChanged}
            />
          )}
          <DestinationCardSwitch
            type={type}
            shopify={shopify}
            existing={existing}
            onSaved={onChanged}
          />
          {existing && (
            <RemoveDestinationRow
              shopify={shopify}
              type={type}
              label={meta.label}
              onRemoved={() => {
                onChanged();
                onClose();
              }}
            />
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// Destructive "remove" control, shown below the form for already-configured
// destinations. Two-step (Remove → confirm) so a misclick doesn't wipe a
// destination's credentials + history. Calls DELETE /api/destinations/:type,
// which removes the row, its delivery history and its dead letters, the
// failed events a credentials save would re-send (critic-1 P4). The confirm
// says so and points at saving new credentials instead. On success the parent
// closes the modal and reloads the hub, so the node disappears from the
// diagram.
function RemoveDestinationRow({
  shopify,
  type,
  label,
  onRemoved,
}: {
  shopify: Shopify;
  type: DestinationType;
  label: string;
  onRemoved: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onRemove = async () => {
    setPending(true);
    setError(null);
    try {
      await apiFetch(shopify, `/api/destinations/${type}`, { method: "DELETE" });
      onRemoved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setError(msg);
      setPending(false);
    }
  };

  return (
    <Box borderColor="border" borderBlockStartWidth="025" paddingBlockStart="300">
      <BlockStack gap="200">
        {!confirming ? (
          <InlineStack align="space-between" blockAlign="center">
            <Text as="span" tone="subdued" variant="bodySm">
              Remove this destination and its delivery history.
            </Text>
            <Button
              tone="critical"
              variant="tertiary"
              onClick={() => setConfirming(true)}
            >
              Remove destination
            </Button>
          </InlineStack>
        ) : (
          <BlockStack gap="200">
            <Text as="p" variant="bodySm">
              Remove {label}? This deletes its credentials, its delivery
              history and any failed events waiting to be re-sent. To recover
              events that failed on bad credentials, save new credentials
              above instead. You can add it again later, but you&rsquo;ll need
              to re-enter credentials.
            </Text>
            <InlineStack gap="200">
              <Button onClick={() => setConfirming(false)} disabled={pending}>
                Cancel
              </Button>
              <Button
                tone="critical"
                variant="primary"
                loading={pending}
                onClick={onRemove}
              >
                Remove
              </Button>
            </InlineStack>
          </BlockStack>
        )}
        {error && (
          <Banner tone="critical">
            <p>{error}</p>
          </Banner>
        )}
      </BlockStack>
    </Box>
  );
}

// Pause/Resume control for an already-configured destination, shown above
// its form in the modal. Mirrors the old DestinationRow toggle; uses the
// PATCH /enabled endpoint so it doesn't require re-entering credentials.
// Resuming re-sends what credentials saved while paused fixed (critic-0 #9);
// the response's counts become the same note a save shows (replay-note.ts).
function PauseResumeRow({
  shopify,
  destination,
  onChanged,
}: {
  shopify: Shopify;
  destination: DestinationView;
  onChanged: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const onToggle = async () => {
    setPending(true);
    setError(null);
    setNote(null);
    try {
      const resp = await apiFetch(shopify, `/api/destinations/${destination.type}/enabled`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !destination.enabled }),
      });
      setNote(replayNote(destination.type, resp));
      onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setError(msg);
    } finally {
      setPending(false);
    }
  };

  return (
    <BlockStack gap="100">
      <InlineStack align="space-between" blockAlign="center">
        <Text as="span" tone="subdued" variant="bodySm">
          {destination.enabled
            ? "Delivering events to this destination"
            : "Delivery is paused"}
        </Text>
        <Button
          size="slim"
          loading={pending}
          onClick={onToggle}
          tone={destination.enabled ? "critical" : undefined}
          variant={destination.enabled ? "tertiary" : "primary"}
        >
          {destination.enabled ? "Pause" : "Resume"}
        </Button>
      </InlineStack>
      {note && (
        <Banner tone="info">
          <p>{note}</p>
        </Banner>
      )}
      {error && (
        <Banner tone="critical">
          <p>{error}</p>
        </Banner>
      )}
    </BlockStack>
  );
}

// Render the right setup form for a destination type. The cards come from
// Onboarding so create + edit + validation stay in one place. Overlap
// warnings are rendered separately by <DestinationOverlapWarning> in the
// modal, so the cards themselves no longer need storeContext.
function DestinationCardSwitch({
  type,
  shopify,
  existing,
  onSaved,
}: {
  type: DestinationType;
  shopify: Shopify;
  existing: DestinationView | undefined;
  onSaved: () => void;
}) {
  // From the registry, not a switch. The switch's `default: return null`
  // meant a destination type with no case rendered an EMPTY modal: the
  // merchant picks a destination and gets a blank panel, with no error
  // anywhere. DESTINATION_CARDS is a Record over DestinationType, so a
  // missing card is a compile error instead.
  const Card = DESTINATION_CARDS[type];
  return <Card shopify={shopify} onSaved={onSaved} existingDestination={existing} />;
}

// ---------- Formatting ----------

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const minutes = Math.floor(diff / (60 * 1000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
